/** Admin → Organizations: the customer list, and creating one (business info
 *  only — people are added inside the org, contract first, people later). */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api';
import { adminApi, fmtDate, type CompanySummary } from '../../adminApi';
import { AdminTopCrumb } from '../../adminShell';
import { AddressInput } from '../../components/AddressInput';
import { StateSelect } from '../../components/StateSelect';

export function Orgs() {
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    adminApi
      .companies()
      .then((d) => setCompanies(d.companies))
      .catch(() => setFailed(true));
  }, []);

  const list = companies ?? [];
  const newest = [...list].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))[0];

  return (
    <>
      <AdminTopCrumb>
        <b>Organizations</b>
      </AdminTopCrumb>
      <main className="page" id="main">
        <div className="page__head">
          <div>
            <h1>Organizations</h1>
            <p className="sub">Every paying customer on Tenpoint</p>
          </div>
          <div className="spacer" />
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            New organization
          </button>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 14 }}>
          <div className="kpi">
            <b>{companies ? list.length : '–'}</b>
            <span>Organizations</span>
          </div>
          <div className="kpi">
            <b>{companies ? list.reduce((n, c) => n + c.userCount, 0) : '–'}</b>
            <span>Users</span>
          </div>
          <div className="kpi">
            <b>{companies ? list.filter((c) => c.status !== 'active').length : '–'}</b>
            <span>Suspended</span>
          </div>
          <div className="kpi">
            <b style={{ fontSize: 16 }}>{newest?.name ?? '—'}</b>
            <span>Newest</span>
          </div>
        </div>

        {creating && <NewOrgPanel onCancel={() => setCreating(false)} />}

        <div className="panel">
          <div className="panel__head">
            <h2>All organizations</h2>
            <div className="spacer" />
            <span className="tag tag--grey">{companies ? `${list.length} total` : '–'}</span>
          </div>
          <div className="table-scroll">
            <table className="tbl tbl--click">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {failed && (
                  <tr>
                    <td colSpan={5} className="dim" style={{ padding: '22px 13px' }}>
                      Could not load organizations.
                    </td>
                  </tr>
                )}
                {companies && list.length === 0 && (
                  <tr>
                    <td colSpan={5} className="dim" style={{ padding: '22px 13px' }}>
                      No organizations yet. Create the first one above.
                    </td>
                  </tr>
                )}
                {list.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/admin/orgs/${c.id}`)}>
                    <td className="strong">{c.name}</td>
                    <td className="num">{c.userCount}</td>
                    <td className="dim">{fmtDate(c.createdAt)}</td>
                    <td>
                      <span className={`tag tag--${c.status === 'active' ? 'ok' : 'warn'}`}>{c.status === 'active' ? 'Active' : c.status}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="dim">Manage ›</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel__foot">Click an organization to manage its profile, properties, units and users.</div>
        </div>
      </main>
    </>
  );
}

function NewOrgPanel({ onCancel }: { onCancel: () => void }) {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const set = (key: string) => (value: string) => setFields((f) => ({ ...f, [key]: value }));
  const bind = (key: string) => ({
    value: fields[key] ?? '',
    onChange: (e: { target: { value: string } }) => set(key)(e.target.value),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    const name = (fields.name ?? '').trim();
    const profile: Record<string, string> = {};
    for (const key of ['address_line1', 'city', 'state', 'postal_code', 'phone']) {
      const value = (fields[key] ?? '').trim();
      if (value) profile[key] = value;
    }

    adminApi
      .createCompany(name)
      .then(async ({ companyId }) => {
        if (Object.keys(profile).length) await adminApi.updateCompany(companyId, profile);
        navigate(`/admin/orgs/${companyId}`);
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'name_required'
            ? 'An organization name is required.'
            : 'Could not create the organization.',
        );
        setBusy(false);
      });
  }

  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div className="panel__head">
        <h2>New organization</h2>
        <div className="spacer" />
        <button className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="panel__body">
        <form className="form" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="c-name">Organization name</label>
            <input id="c-name" type="text" required placeholder="Riverbend Housing Group" {...bind('name')} />
          </div>
          <div className="field">
            <label htmlFor="c-address1">Mailing address</label>
            <AddressInput
              id="c-address1"
              value={fields.address_line1 ?? ''}
              onChange={set('address_line1')}
              onPlace={(p) =>
                setFields((f) => ({
                  ...f,
                  address_line1: p.line1 || (f.address_line1 ?? ''),
                  city: p.city || (f.city ?? ''),
                  state: p.state || (f.state ?? ''),
                  postal_code: p.zip || (f.postal_code ?? ''),
                }))
              }
            />
          </div>
          <div className="field--row">
            <div className="field">
              <label htmlFor="c-city">City</label>
              <input id="c-city" type="text" {...bind('city')} />
            </div>
            <div className="field--row">
              <div className="field">
                <label htmlFor="c-state">State</label>
                <StateSelect id="c-state" {...bind('state')} />
              </div>
              <div className="field">
                <label htmlFor="c-zip">ZIP</label>
                <input id="c-zip" type="text" inputMode="numeric" {...bind('postal_code')} />
              </div>
            </div>
          </div>
          <div className="field" style={{ maxWidth: 280 }}>
            <label htmlFor="c-phone">Phone</label>
            <input id="c-phone" type="tel" placeholder="312-555-0100" {...bind('phone')} />
          </div>
          {error && <p className="login__error">{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
